import React, { ReactNode, cloneElement, isValidElement } from "react";

type TextComponents = {
  [key: string]: ReactNode;
};

interface ParsedTextProps {
  text: string;
  components: TextComponents;
}

function parseText(
  text: string,
  components: Record<string, ReactNode>
): ReactNode[] {
  // Regular expression to match <tag>content</tag>, <tag/>, and <tag /> formats
  const regex = /<(\w+)(?:\s*\/>|>(.*?)<\/\1>)/g;

  // Process the text recursively to handle nested tags
  const processText = (inputText: string): ReactNode => {
    // Check if the text contains any tags
    if (!/<\w+/.test(inputText)) {
      return inputText;
    }

    const parts: ReactNode[] = [];
    let lastIdx = 0;
    let m: RegExpExecArray | null;
    const localRegex = new RegExp(regex);

    while ((m = localRegex.exec(inputText)) !== null) {
      // Add text before the match
      if (m.index > lastIdx) {
        parts.push(inputText.slice(lastIdx, m.index));
      }

      const tagName = m[1];
      const content = m[2] || ""; // Will be undefined for self-closing tags

      // Process the content recursively to handle nested tags
      const processedContent = content ? processText(content) : null;

      // Add the JSX content if component exists, otherwise add plain text
      if (components[tagName]) {
        const Component = components[tagName];
        if (isValidElement(Component)) {
          // Clone the element with the processed content as children if there is content
          parts.push(
            cloneElement(Component as React.ReactElement, {
              key: m.index,
              // Use processed content if available, otherwise keep original children
              ...(processedContent ? { children: processedContent } : {}),
            })
          );
        } else {
          // Handle non-element components (like functions or strings)
          parts.push(Component);
        }
      } else if (processedContent) {
        parts.push(processedContent);
      }

      lastIdx = localRegex.lastIndex;
    }

    // Add remaining text after last match
    if (lastIdx < inputText.length) {
      parts.push(inputText.slice(lastIdx));
    }

    return parts.length === 1 ? parts[0] : parts;
  };

  return [processText(text)].flat();
}

export default function ParsedText({
  text,
  components,
}: ParsedTextProps): JSX.Element {
  return <>{parseText(text, components)}</>;
}
